import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";
import {
  addButtonPadding,
  registerPatientButtonStyles,
} from "../../common/commonStyles";
import ReusableSnackbar from "../../shared/Alert/ReusableSnackbar";
import validators from "../../utils/validations/validators";
import api from "../../services/api";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import EventIcon from "@mui/icons-material/Event";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import PlaceIcon from "@mui/icons-material/Place";
import DeleteIcon from "@mui/icons-material/Delete";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import "../SettingsManagement/SettingManagement.css";

const BRAND_COLOR = "#4b49ac";

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

export default function SettingManagement({ userDetail }) {
  const [activeTab, setActiveTab] = useState(0);

  // ---------- Password reset state ----------
  const [passwordData, setPasswordData] = useState({
    new_password: "",
    email: userDetail?.email,
    confirm_password: "",
  });

  // ---------- Mail configuration state ----------
  const [mailData, setMailData] = useState({
    email_type: "",
    email_host: "",
    email_port: "",
    email: "",
    email_password: "",
  });
  const [mailDetails, setMailDetails] = useState();

  // ---------- Holiday management state ----------
  const branches = userDetail?.branches || [];
  const [holidayData, setHolidayData] = useState({
    branch_id: "",
    holiday_date: "",
    holiday_name: "",
    description: "",
  });
  const [holidays, setHolidays] = useState([]);
  const [holidayBranchFilter, setHolidayBranchFilter] = useState("all");

  // ---------- Branch Signatories state ----------
  const [signatoryBranchId, setSignatoryBranchId] = useState(
    branches?.[0]?.id || ""
  );
  const [signatoryData, setSignatoryData] = useState(null);
  const [signatoryDraft, setSignatoryDraft] = useState({
    founder_id: "",
    co_founder_id: "",
    director_id: "",
    active_signatories: [],
  });
  const [signatoryLoading, setSignatoryLoading] = useState(false);
  const [signatorySaving, setSignatorySaving] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState({
    status: false,
    message: "",
    severity: "",
  });
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    getMailconfigDeatils();
    getHolidays();
  }, []);

  useEffect(() => {
    if (activeTab === 3 && signatoryBranchId) {
      fetchSignatories(signatoryBranchId);
    }
  }, [activeTab, signatoryBranchId]);

  const getMailconfigDeatils = async () => {
    try {
      let response = await api.get("app/messaging/mail");
      setMailDetails(response?.data);
    } catch (error) {
      console.error("Unexpected error:", error);
    }
  };

  // branch_id is now optional on the API - omit it to get holidays across all branches
  // NOTE: DELETE assumed to follow the same pattern as the given POST /app/holidays/
  const getHolidays = async (branchFilter = holidayBranchFilter) => {
    try {
      const query = branchFilter !== "all" ? `?branch_id=${branchFilter}` : "";
      const response = await api.get(`app/holidays/${query}`);
      setHolidays(response?.data || []);
    } catch (error) {
      console.error("Unexpected error:", error);
    }
  };

  const fetchSignatories = async (branchId) => {
    setSignatoryLoading(true);
    try {
      const response = await api.get(`app/management/branch/${branchId}/signatories`);
      const data = response?.data;
      setSignatoryData(data);
      setSignatoryDraft({
        founder_id: data?.founder_id || "",
        co_founder_id: data?.co_founder_id || "",
        director_id: data?.director_id || "",
        active_signatories: data?.active_signatories || [],
      });
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message: "Failed to load signatory configuration.",
        severity: "error",
      });
    } finally {
      setSignatoryLoading(false);
    }
  };

  const handleSnackClose = () => setSnackbarOpen(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (validators[name]) {
      const errorMsg = validators[name](value);
      setValidationErrors((prev) => ({
        ...prev,
        [name]: errorMsg,
      }));
    }
  };

  const handleMailInputChange = (e) => {
    const { name, value } = e.target;
    setMailData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (validators[name]) {
      const errorMsg = validators[name](value);
      setValidationErrors((prev) => ({
        ...prev,
        [name]: errorMsg,
      }));
    }
  };

  const handleHolidayInputChange = (e) => {
    const { name, value } = e.target;
    setHolidayData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleResetPassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      setSnackbarOpen({
        status: true,
        message: "Passwords do not match!",
        severity: "error",
      });
      return;
    }
    try {
      const payload = {
        email: userDetail?.email,
        new_password: passwordData.new_password,
      };
      const response = await api.post(`app/reset-password-by-email`, payload);

      if (response.status === 200) {
        setPasswordData({
          new_password: "",
          confirm_password: "",
        });
        setSnackbarOpen({
          status: true,
          message: "Password has been reset successfully!",
          severity: "success",
        });
      }
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message:
          error?.response?.data?.detail[0].msg ||
          "Something went wrong ! Please try again",
        severity: "error",
      });
    }
  };

  const handleConfigureMail = async () => {
    try {
      const response = await api.post(`app/messaging/mail`, mailData);
      if (response.status === 201) {
        setMailData({
          email_type: "",
          email_host: "",
          email_port: "",
          email: "",
          email_password: "",
        });
        setSnackbarOpen({
          status: true,
          message: "Mail configuration added!",
          severity: "success",
        });
        await getMailconfigDeatils();
      }
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message: "Something went wrong ! Please try again",
        severity: "error",
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await api.delete(`app/messaging/${id}`);
      if (response.status === 200) {
        setSnackbarOpen({
          status: true,
          message: "Mail deleted successfully !",
          severity: "success",
        });
        await getMailconfigDeatils();
      }
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message: "Something went wrong ! Please try again",
        severity: "error",
      });
    }
  };

  const handleAddHoliday = async () => {
    try {
      const payload = {
        ...holidayData,
        branch_id: Number(holidayData.branch_id),
      };
      const response = await api.post(`app/holidays/`, payload);
      if (response.status === 200 || response.status === 201) {
        setHolidayData({
          branch_id: "",
          holiday_date: "",
          holiday_name: "",
          description: "",
        });
        setSnackbarOpen({
          status: true,
          message: "Holiday added successfully!",
          severity: "success",
        });
        await getHolidays(holidayBranchFilter);
      }
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message:
          error?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong ! Please try again",
        severity: "error",
      });
    }
  };

  const handleDeleteHoliday = async (id) => {
    try {
      const response = await api.delete(`app/holidays/${id}`);
      if (response.status === 200) {
        setSnackbarOpen({
          status: true,
          message: "Holiday deleted successfully!",
          severity: "success",
        });
        await getHolidays(holidayBranchFilter);
      }
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message: "Something went wrong ! Please try again",
        severity: "error",
      });
    }
  };

  const handleSignatoryFieldChange = (field, value) => {
    setSignatoryDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleActiveSignatoryToggle = (role) => {
    setSignatoryDraft((prev) => {
      const isActive = prev.active_signatories.includes(role);
      return {
        ...prev,
        active_signatories: isActive
          ? prev.active_signatories.filter((r) => r !== role)
          : [...prev.active_signatories, role],
      };
    });
  };

  const handleSaveSignatories = async () => {
    setSignatorySaving(true);
    try {
      const payload = {
        founder_id: signatoryDraft.founder_id || null,
        co_founder_id: signatoryDraft.co_founder_id || null,
        director_id: signatoryDraft.director_id || null,
        active_signatories: signatoryDraft.active_signatories,
      };
      const response = await api.patch(
        `app/management/branch/${signatoryBranchId}/signatories`,
        payload
      );
      setSignatoryData(response?.data);
      setSnackbarOpen({
        status: true,
        message: "Signatory configuration updated!",
        severity: "success",
      });
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message:
          error?.response?.data?.detail ||
          "Something went wrong! Please try again",
        severity: "error",
      });
    } finally {
      setSignatorySaving(false);
    }
  };

  const getBranchName = (id) =>
    branches.find((b) => b.id === id)?.name || `Branch ${id}`;

  const getDayNumber = (dateStr) => new Date(dateStr).getDate();
  const getMonthAbbr = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-IN", { month: "short" });
  const getWeekday = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-IN", { weekday: "long" });
  const getMonthYearLabel = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });

  const isFormValid =
    Object.values(validationErrors).every((err) => err === "") &&
    Object.values(passwordData).every((val) => val !== "" && val !== null);

  const isconfigMailValid =
    Object.values(validationErrors).every((err) => err === "") &&
    Object.values(mailData).every((val) => val !== "" && val !== null);

  const isHolidayFormValid = Object.values(holidayData).every(
    (val) => val !== "" && val !== null
  );

  // holidays is already scoped to the selected branch filter by getHolidays()
  const visibleHolidays = holidays;

  // Backend returns holidays ordered by date, so grouping preserves chronological order
  const groupedHolidays = visibleHolidays.reduce((acc, item) => {
    const label = getMonthYearLabel(item.holiday_date);
    if (!acc[label]) acc[label] = [];
    acc[label].push(item);
    return acc;
  }, {});

  const handleHolidayFilterChange = (branchFilter) => {
    setHolidayBranchFilter(branchFilter);
    getHolidays(branchFilter);
  };

  return (
    <Box className="setting_section" sx={{ maxWidth: 1100 }}>
      <Tabs
        value={activeTab}
        onChange={(e, val) => setActiveTab(val)}
        sx={{
          mb: 3,
          borderBottom: "1px solid #e5e5ef",
          "& .MuiTab-root": {
            textTransform: "none",
            fontFamily: "Nunito, sans-serif",
            fontWeight: 600,
            minHeight: 48,
          },
          "& .Mui-selected": { color: `${BRAND_COLOR} !important` },
          "& .MuiTabs-indicator": { backgroundColor: BRAND_COLOR },
        }}
      >
        <Tab
          icon={<LockIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Security"
        />
        <Tab
          icon={<EmailIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Mail Configuration"
        />
        <Tab
          icon={<EventIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Holiday Management"
        />
        <Tab
          icon={<AssignmentIndIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Branch Signatories"
        />
      </Tabs>

      {/* ---------------- SECURITY TAB ---------------- */}
      <TabPanel value={activeTab} index={0}>
        <h4>Reset Your Password</h4>
        <p>
          Time to set a new password 😊 Enter and confirm your new password
          below. Keep it strong and secure!
        </p>
        <Grid container spacing={2}>
          <Grid size={5}>
            <TextField
              required
              fullWidth
              margin="normal"
              label="New Password"
              name="new_password"
              size="small"
              value={passwordData?.new_password}
              onChange={handleInputChange}
              autoComplete="off"
              className="custom-textfield"
              error={!!validationErrors.new_password}
              helperText={validationErrors.new_password}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ marginBottom: "20px" }}>
          <Grid size={5}>
            <TextField
              required
              fullWidth
              margin="normal"
              label="Confirm Password"
              name="confirm_password"
              size="small"
              value={passwordData?.confirm_password}
              onChange={handleInputChange}
              autoComplete="off"
              className="custom-textfield"
              error={!!validationErrors.confirm_password}
              helperText={validationErrors.confirm_password}
            />
          </Grid>
        </Grid>
        <Button
          variant="contained"
          sx={[registerPatientButtonStyles, addButtonPadding]}
          size="small"
          onClick={() => handleResetPassword()}
          disabled={!isFormValid}
        >
          Change Password
        </Button>
      </TabPanel>

      {/* ---------------- MAIL CONFIGURATION TAB ---------------- */}
      <TabPanel value={activeTab} index={1}>
        <h4>Mail Configuration</h4>
        <p>Add a mail configuration for your communication.</p>
        <Grid container spacing={2} className="align-items-center">
          <Grid size={2}>
            <TextField
              required
              fullWidth
              margin="normal"
              label="Email Type"
              name="email_type"
              size="small"
              value={mailData?.email_type}
              onChange={handleMailInputChange}
              autoComplete="off"
              className="custom-textfield"
              error={!!validationErrors.email_type}
              helperText={validationErrors.email_type}
            />
          </Grid>
          <Grid size={2}>
            <TextField
              required
              fullWidth
              margin="normal"
              label="Host"
              name="email_host"
              size="small"
              value={mailData?.email_host}
              onChange={handleMailInputChange}
              autoComplete="off"
              className="custom-textfield"
              error={!!validationErrors.email_host}
              helperText={validationErrors.email_host}
            />
          </Grid>
          <Grid size={1}>
            <TextField
              required
              fullWidth
              margin="normal"
              label="Port"
              name="email_port"
              size="small"
              value={mailData?.email_port}
              onChange={handleMailInputChange}
              autoComplete="off"
              className="custom-textfield"
              error={!!validationErrors.email_port}
              helperText={validationErrors.email_port}
            />
          </Grid>
          <Grid size={2}>
            <TextField
              required
              fullWidth
              margin="normal"
              label="Email"
              name="email"
              size="small"
              value={mailData?.email}
              onChange={handleMailInputChange}
              autoComplete="off"
              className="custom-textfield"
              error={!!validationErrors.email}
              helperText={validationErrors.email}
            />
          </Grid>
          <Grid size={2}>
            <TextField
              required
              fullWidth
              margin="normal"
              label="Email Password"
              name="email_password"
              size="small"
              value={mailData?.email_password}
              onChange={handleMailInputChange}
              autoComplete="off"
              className="custom-textfield"
              error={!!validationErrors.email_password}
              helperText={validationErrors.email_password}
            />
          </Grid>
          <Grid size={2} sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="contained"
              sx={[registerPatientButtonStyles, addButtonPadding]}
              size="small"
              onClick={() => handleConfigureMail()}
              disabled={!isconfigMailValid}
            >
              Save
            </Button>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          {mailDetails?.map((item) => (
            <Grid size={4} key={item.id}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: 2,
                  padding: 1,
                  marginTop: 2,
                  position: "relative",
                  "&:hover .deleteBtn": { opacity: 1 },
                }}
              >
                <IconButton
                  className="deleteBtn"
                  onClick={() => handleDelete(item.id)}
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    opacity: 0,
                    transition: "0.3s",
                    color: "red",
                  }}
                >
                  <DeleteIcon sx={{ fontSize: "17px" }} />
                </IconButton>
                <CardContent sx={{ display: "flex", gap: 2 }}>
                  <Avatar sx={{ width: 48, height: 48, bgcolor: BRAND_COLOR }}>
                    <EmailIcon />
                  </Avatar>
                  <Box>
                    <Typography
                      sx={{
                        color: BRAND_COLOR,
                        fontWeight: 600,
                        fontSize: "16px",
                        textTransform: "capitalize",
                        fontFamily: "Nunito, sans-serif",
                      }}
                    >
                      {item.email_type} Email
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#333",
                        mt: 0.5,
                        fontFamily: "Nunito, sans-serif",
                      }}
                    >
                      {item.email}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "gray",
                        mt: 0.5,
                        fontFamily: "Nunito, sans-serif",
                      }}
                    >
                      Host: {item.email_host}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "gray",
                        fontFamily: "Nunito, sans-serif",
                      }}
                    >
                      Port: {item.email_port}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* ---------------- HOLIDAY MANAGEMENT TAB ---------------- */}
      <TabPanel value={activeTab} index={2}>
        <h4>Holiday Management</h4>
        <p>
          Add clinic holidays for a specific branch. Each holiday applies
          only to the branch it's added for.
        </p>

        <Grid container spacing={2} className="align-items-center">
          <Grid size={2}>
            <FormControl
              fullWidth
              margin="normal"
              size="small"
              className="custom-textfield"
            >
              <InputLabel>Branch</InputLabel>
              <Select
                required
                label="Branch"
                name="branch_id"
                value={holidayData.branch_id}
                onChange={handleHolidayInputChange}
              >
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={2}>
            <TextField
              required
              fullWidth
              margin="normal"
              label="Holiday Date"
              name="holiday_date"
              type="date"
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              value={holidayData.holiday_date}
              onChange={handleHolidayInputChange}
              className="custom-textfield"
            />
          </Grid>
          <Grid size={3}>
            <TextField
              required
              fullWidth
              margin="normal"
              label="Holiday Name"
              name="holiday_name"
              size="small"
              value={holidayData.holiday_name}
              onChange={handleHolidayInputChange}
              autoComplete="off"
              className="custom-textfield"
            />
          </Grid>
          <Grid size={3}>
            <TextField
              fullWidth
              margin="normal"
              label="Description"
              name="description"
              size="small"
              value={holidayData.description}
              onChange={handleHolidayInputChange}
              autoComplete="off"
              className="custom-textfield"
            />
          </Grid>
          <Grid size={2} sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="contained"
              sx={[registerPatientButtonStyles, addButtonPadding]}
              size="small"
              onClick={() => handleAddHoliday()}
              disabled={!isHolidayFormValid}
            >
              Add Holiday
            </Button>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mt: 2,
            mb: 1,
          }}
        >
          <Typography
            sx={{ fontSize: "13px", color: "gray", fontFamily: "Nunito, sans-serif", mr: 0.5 }}
          >
            Filter:
          </Typography>
          <Chip
            label="All Branches"
            size="small"
            onClick={() => handleHolidayFilterChange("all")}
            sx={{
              bgcolor: holidayBranchFilter === "all" ? BRAND_COLOR : "#eee",
              color: holidayBranchFilter === "all" ? "#fff" : "#333",
              fontFamily: "Nunito, sans-serif",
            }}
          />
          {branches.map((branch) => (
            <Chip
              key={branch.id}
              label={branch.name}
              size="small"
              onClick={() => handleHolidayFilterChange(branch.id)}
              sx={{
                bgcolor:
                  holidayBranchFilter === branch.id ? BRAND_COLOR : "#eee",
                color: holidayBranchFilter === branch.id ? "#fff" : "#333",
                fontFamily: "Nunito, sans-serif",
              }}
            />
          ))}
        </Box>

        {visibleHolidays?.length ? (
          Object.entries(groupedHolidays).map(([month, items]) => (
            <Box key={month} sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "gray",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 1,
                  fontFamily: "Nunito, sans-serif",
                }}
              >
                {month}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {items.map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 1.25,
                      borderRadius: 2,
                      border: "1px solid #edecf6",
                      "&:hover": { bgcolor: "#faf9fd" },
                      "&:hover .deleteBtn": { opacity: 1 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        minWidth: 52,
                        textAlign: "center",
                        bgcolor: "#efeefb",
                        color: BRAND_COLOR,
                        borderRadius: 1.5,
                        py: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "18px",
                          fontWeight: 700,
                          lineHeight: 1,
                          fontFamily: "Nunito, sans-serif",
                        }}
                      >
                        {getDayNumber(item.holiday_date)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 600,
                          fontFamily: "Nunito, sans-serif",
                        }}
                      >
                        {getMonthAbbr(item.holiday_date)}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#222",
                          fontFamily: "Nunito, sans-serif",
                        }}
                      >
                        {item.holiday_name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "gray",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontFamily: "Nunito, sans-serif",
                        }}
                      >
                        {getWeekday(item.holiday_date)}
                        {holidayBranchFilter === "all" && (
                          <>
                            <PlaceIcon sx={{ fontSize: "12px", ml: 0.5 }} />
                            {getBranchName(item.branch_id)}
                          </>
                        )}
                      </Typography>
                      {item.description && (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#888",
                            mt: 0.25,
                            fontFamily: "Nunito, sans-serif",
                          }}
                        >
                          {item.description}
                        </Typography>
                      )}
                    </Box>

                    <IconButton
                      className="deleteBtn"
                      onClick={() => handleDeleteHoliday(item.id)}
                      sx={{ opacity: 0, transition: "0.3s", color: "red" }}
                    >
                      <DeleteIcon sx={{ fontSize: "17px" }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          ))
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 4,
              color: "gray",
            }}
          >
            <EventBusyIcon sx={{ fontSize: 36, mb: 1 }} />
            <Typography sx={{ fontFamily: "Nunito, sans-serif" }}>
              No holidays added yet
              {holidayBranchFilter !== "all" ? " for this branch" : ""}.
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* ---------------- BRANCH SIGNATORIES TAB ---------------- */}
      <TabPanel value={activeTab} index={3}>
        <h4>Branch Signatories</h4>
        <p>
          Assign the Founder, Co-Founder, and Director for a branch, and
          choose which of them appear on that branch's offer letters.
        </p>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={3}>
            <FormControl
              fullWidth
              margin="normal"
              size="small"
              className="custom-textfield"
            >
              <InputLabel>Branch</InputLabel>
              <Select
                label="Branch"
                value={signatoryBranchId}
                onChange={(e) => setSignatoryBranchId(e.target.value)}
              >
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {signatoryLoading ? (
          <Typography sx={{ color: "gray", fontFamily: "Nunito, sans-serif" }}>
            Loading...
          </Typography>
        ) : signatoryData ? (
          <>
            <Grid container spacing={2}>
              {[
                { field: "founder_id", label: "Founder" },
                { field: "co_founder_id", label: "Co-Founder" },
                { field: "director_id", label: "Director" },
              ].map(({ field, label }) => (
                <Grid size={3} key={field}>
                  <FormControl
                    fullWidth
                    margin="normal"
                    size="small"
                    className="custom-textfield"
                  >
                    <InputLabel>{label}</InputLabel>
                    <Select
                      label={label}
                      value={signatoryDraft[field]}
                      onChange={(e) =>
                        handleSignatoryFieldChange(field, e.target.value)
                      }
                    >
                      <MenuItem value="">— None —</MenuItem>
                      {signatoryData.eligible_users?.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.name} ({u.role})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
            </Grid>

            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                color: "gray",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                mt: 3,
                mb: 1,
                fontFamily: "Nunito, sans-serif",
              }}
            >
              Show on Offer Letter
            </Typography>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              {[
                { role: "FOUNDER", label: "Founder", idField: "founder_id" },
                {
                  role: "CO_FOUNDER",
                  label: "Co-Founder",
                  idField: "co_founder_id",
                },
                { role: "DIRECTOR", label: "Director", idField: "director_id" },
              ].map(({ role, label, idField }) => {
                const isAssigned = !!signatoryDraft[idField];
                const isActive =
                  signatoryDraft.active_signatories.includes(role);
                return (
                  <Chip
                    key={role}
                    label={label}
                    size="small"
                    onClick={() =>
                      isAssigned && handleActiveSignatoryToggle(role)
                    }
                    disabled={!isAssigned}
                    sx={{
                      bgcolor: isActive ? BRAND_COLOR : "#eee",
                      color: isActive ? "#fff" : "#333",
                      fontFamily: "Nunito, sans-serif",
                      opacity: isAssigned ? 1 : 0.5,
                      cursor: isAssigned ? "pointer" : "not-allowed",
                    }}
                  />
                );
              })}
            </Box>

            <Button
              variant="contained"
              sx={[registerPatientButtonStyles, addButtonPadding]}
              size="small"
              onClick={handleSaveSignatories}
              disabled={signatorySaving}
            >
              {signatorySaving ? "Saving..." : "Save"}
            </Button>
          </>
        ) : (
          <Typography sx={{ color: "gray", fontFamily: "Nunito, sans-serif" }}>
            Select a branch to configure signatories.
          </Typography>
        )}
      </TabPanel>

      <ReusableSnackbar
        open={snackbarOpen.status}
        message={snackbarOpen.message}
        onClose={handleSnackClose}
        severity={snackbarOpen.severity}
      />
    </Box>
  );
}